import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import styles from './styles.js';
import type { MenuItem, MenuItemSelectEventDetail } from '../types.js';
import { emit } from '../utils/dom.utils.js';

/* ─────────────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────────────── */

/**
 * `uwc-breadcrumb`
 *
 * A navigation trail showing the user's current location within a site hierarchy.
 * Follows the WAI-ARIA breadcrumb pattern: `<nav aria-label="Breadcrumb">` wraps
 * an `<ol>` list; the last item receives `aria-current="page"` and is rendered
 * as a non-interactive span. All other items are `<a>` or `<button>` elements.
 *
 * @attr model     — `MenuItem[]` breadcrumb items. Last item = current page.
 * @attr separator — Separator string. Empty (default) → chevron-right icon.
 *                   Any other value (e.g. `"/"`, `"»"`) is rendered as text.
 *
 * @prop home      — `MenuItem` shown at the leading edge. Set via JS property.
 *                   When `icon` is omitted the default `house` icon is used.
 *
 * @fires uwc-item-select — `{ item: MenuItem, originalEvent: Event }` when any
 *                          non-current item is activated.
 *
 * @cssprop --uwc-breadcrumb-bg
 * @cssprop --uwc-breadcrumb-border
 * @cssprop --uwc-breadcrumb-padding
 * @cssprop --uwc-breadcrumb-radius
 * @cssprop --uwc-breadcrumb-item-color
 * @cssprop --uwc-breadcrumb-item-hover-color
 * @cssprop --uwc-breadcrumb-item-hover-bg
 * @cssprop --uwc-breadcrumb-item-font-size
 * @cssprop --uwc-breadcrumb-item-font-weight
 * @cssprop --uwc-breadcrumb-item-disabled-color
 * @cssprop --uwc-breadcrumb-active-item-color
 * @cssprop --uwc-breadcrumb-active-item-font-weight
 * @cssprop --uwc-breadcrumb-separator-color
 * @cssprop --uwc-breadcrumb-sep-gap
 * @cssprop --uwc-breadcrumb-home-color
 */
export class UwcBreadcrumb extends LitElement {
  static override styles = styles;

  /* ── Public properties ─────────────────────────────────────────────── */

  @property({ type: Array })
  model: MenuItem[] = [];

  /**
   * Home item rendered at the leading edge.
   * Set via JavaScript: `el.home = { url: '/', icon: 'house' }`.
   * When `icon` is not provided, the `house` Bootstrap icon is used by default.
   */
  @property({ attribute: false })
  home: MenuItem | null = null;

  /**
   * Separator between items.
   * - Empty string (default) → renders a `chevron-right` icon.
   * - Any other string (e.g. `"/"`, `"›"`) → rendered as plain text.
   */
  @property()
  separator = '';

  /* ── Private helpers ────────────────────────────────────────────────── */

  private _execute(item: MenuItem, e: Event): void {
    if (item.disabled) return;
    item.command?.({ originalEvent: e, item });
    emit<MenuItemSelectEventDetail>(this, 'uwc-item-select', { item, originalEvent: e });
  }

  private _sep(): TemplateResult {
    return this.separator
      ? html`<span class="uwc-bc__sep-text">${this.separator}</span>`
      : html`<span class="uwc-bc__sep-icon"><uwc-icon name="chevron-right" size="0.8em"></uwc-icon></span>`;
  }

  private _renderLink(item: MenuItem, isCurrent: boolean, isHome: boolean): TemplateResult {
    const showIcon = item.icon || (isHome && !item.label);

    const inner = html`
      ${showIcon
        ? html`<span class="uwc-bc__icon" aria-hidden="true">
            <uwc-icon name=${item.icon ?? 'house'} size="1em"></uwc-icon>
          </span>`
        : nothing}
      ${item.label
        ? html`<span class="uwc-bc__label">${item.label}</span>`
        : nothing}`;

    const cls = classMap({
      'uwc-bc__link':           true,
      'uwc-bc__link--home':     isHome,
      'uwc-bc__link--current':  isCurrent,
      'uwc-bc__link--disabled': !!item.disabled,
    });

    // Current page — non-interactive span
    if (isCurrent) {
      return html`
        <span class=${cls} aria-current="page" title=${item.title ?? ''}>
          ${inner}
        </span>`;
    }

    // Anchor (url provided and not disabled)
    if (item.url && !item.disabled) {
      return html`
        <a
          class=${cls}
          href=${item.url}
          target=${item.target ?? '_self'}
          title=${item.title ?? ''}
          @click=${(e: MouseEvent) => this._execute(item, e)}
        >${inner}</a>`;
    }

    // Button (command only, or home without url)
    return html`
      <button
        type="button"
        class=${cls}
        ?disabled=${item.disabled}
        title=${item.title ?? ''}
        @click=${(e: MouseEvent) => this._execute(item, e)}
      >${inner}</button>`;
  }

  /* ── Render ─────────────────────────────────────────────────────────── */

  protected override render(): TemplateResult {
    const visible = this.model.filter(it => it.visible !== false);

    type Entry = { item: MenuItem; isCurrent: boolean; isHome: boolean };
    const entries: Entry[] = [];

    if (this.home) {
      entries.push({ item: this.home, isCurrent: false, isHome: true });
    }
    visible.forEach((item, i) => {
      entries.push({ item, isCurrent: i === visible.length - 1, isHome: false });
    });

    return html`
      <nav class="uwc-bc" aria-label="Breadcrumb">
        <ol class="uwc-bc__list" role="list">
          ${entries.map(({ item, isCurrent, isHome }, idx) => html`
            <li class=${classMap({
              'uwc-bc__item':          true,
              'uwc-bc__item--home':    isHome,
              'uwc-bc__item--current': isCurrent,
            })}>
              ${this._renderLink(item, isCurrent, isHome)}
            </li>
            ${idx < entries.length - 1
              ? html`<li class="uwc-bc__sep" role="presentation" aria-hidden="true">
                  ${this._sep()}
                </li>`
              : nothing}
          `)}
        </ol>
      </nav>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-breadcrumb': UwcBreadcrumb; }
}
